import React, { useEffect, useState } from 'react';
import TaskModal from '../components/AddTaskModal';
import TaskCard, { Task } from '../components/TaskCard';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import defaultProfile from '../assets/default-profile.png';
import { ref, set, onDisconnect, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { useContext } from 'react';
import UserCursor from '../components/UserCursor';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
// import plus icon
import { PlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface KanbanBoard {
  columns: {
    todo: Task[];
    inProgress: Task[];
    done: Task[];
  };
  createdAt: number;
}

const KanbanBoardPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const authCtx=useContext(AuthContext);
  const currentUser=authCtx?.user;
  const boardTag='kanban';
  const [onlineMembers,setOnlineMembers]=useState<any[]>([]);
  const [board, setBoard] = useState<KanbanBoard | null>(null);
  const [showModalCol, setShowModalCol] = useState<keyof KanbanBoard['columns'] | null>(null);
  const [editData, setEditData] = useState<{col:keyof KanbanBoard['columns']; task: Task} | null>(null);
  const [team, setTeam] = useState<any>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<{task: Task, col: keyof KanbanBoard['columns']} | null>(null);

  // Handle moving task between columns
  const moveTask = async (task: Task, sourceCol: keyof KanbanBoard['columns'], targetCol: keyof KanbanBoard['columns']) => {
    if (!board || !teamId || sourceCol === targetCol) return;
    
    console.log('✅ Moving task:', { 
      task: task.title, 
      from: sourceCol, 
      to: targetCol
    });
    
    const updated = { ...(board as KanbanBoard) };
    
    // Remove from source
    updated.columns[sourceCol] = updated.columns[sourceCol].filter(t => t.id !== task.id);
    
    // Add to target
    updated.columns[targetCol].push(task);
    
    setBoard(updated);
    setSelectedTask(null); // Clear selection after move
    
    try {
      await setDoc(doc(firestore,'kanbanBoards',teamId), updated);
      console.log('💾 Task moved successfully to Firebase');
    } catch (error) {
      console.error('❌ Error moving task:', error);
    }
  };

  // Check team membership and permissions
  useEffect(() => {
    if (!teamId || !currentUser?.uid) {
      console.log('Missing teamId or currentUser:', { teamId, currentUserUid: currentUser?.uid });
      setLoading(false);
      return;
    }

    console.log('Setting up team listener for:', { teamId, currentUserUid: currentUser.uid });

    const teamRef = ref(database, `teams/${teamId}`);
    const unsubscribe = onValue(teamRef, (snapshot) => {
      const teamData = snapshot.val();
      console.log('Team data received:', teamData);
      setTeam(teamData);
      
      // TEMPORARILY DISABLE PERMISSION CHECKS - ALWAYS ALLOW EDIT
      console.log('TEMPORARILY SETTING canEdit to TRUE for debugging');
      setCanEdit(true);
      
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [teamId, currentUser]);

  useEffect(() => {
    if (!teamId) return;
    const ref = doc(firestore, 'kanbanBoards', teamId);

    const init = async () => {
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        const data: KanbanBoard = {
          columns: { todo: [], inProgress: [], done: [] },
          createdAt: Date.now(),
        };
        await setDoc(ref, data);
      }
    };
    init();

    return onSnapshot(ref, (snap) => {
      const data = snap.data() as KanbanBoard | undefined;
      if (data) setBoard(data);
    });
  }, [teamId]);

  useEffect(()=>{
    if(!teamId || !currentUser) return;

    const presRef = ref(database, `teams/${teamId}/presence/${currentUser.uid}`);
    
    // Function to update presence with current timestamp and position
    const updatePresence = (x: number = 0, y: number = 0) => {
      set(presRef, { 
        uid: currentUser.uid, 
        displayName: currentUser.displayName||'User', 
        photoURL: currentUser.photoURL||'', 
        board: boardTag, 
        x, 
        y, 
        lastActive: Date.now() 
      });
    };
    
    // Initial presence update
    updatePresence();
    
    // Set up heartbeat to update presence every 30 seconds
    const heartbeatInterval = setInterval(() => updatePresence(), 30000);
    
    // Remove presence on disconnect
    onDisconnect(presRef).remove();
    
    // Clean up presence on beforeunload
    const handleBeforeUnload = () => {
      set(presRef, null);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    const handleMove = (()=>{
      let last=0;
      return (e:MouseEvent)=>{
        const now=Date.now();
        if(now-last<80) return; // throttle ~12fps
        last=now;
        updatePresence(e.clientX, e.clientY);
      };
    })();
    window.addEventListener('mousemove', handleMove);
    
    // Listen for all presence data
    const listRef = ref(database, `teams/${teamId}/presence`);
    const unsub = onValue(listRef, (snap) => {
      const data = snap.val();
      if (data) {
        const currentTime = Date.now();
        const activeThreshold = 2 * 60 * 1000; // 2 minutes
        
        // Filter out stale presence data
        const activePresence: any = {};
        Object.entries(data).forEach(([uid, presence]: [string, any]) => {
          if (presence && presence.lastActive && (currentTime - presence.lastActive) < activeThreshold) {
            activePresence[uid] = presence;
          } else {
            // Remove stale presence data
            const staleRef = ref(database, `teams/${teamId}/presence/${uid}`);
            set(staleRef, null);
          }
        });
        
        setOnlineMembers(Object.values(activePresence));
      } else {
        setOnlineMembers([]);
      }
    });
    
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('mousemove', handleMove);
      set(presRef, null); // Remove presence when component unmounts
      unsub();
    };
  }, [teamId,currentUser]);

  if (!board || loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  // TEMPORARILY DISABLED: Show access denied if user is not a team member
  // if (!canEdit && team) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <p className="text-gray-500 mb-4">You don't have permission to edit this Kanban board.</p>
  //         <p className="text-sm text-gray-400 mb-4">Contact a team admin to get edit access.</p>
  //         <button 
  //           onClick={() => navigate('/dashboard')} 
  //           className="text-blue-600 hover:text-blue-700"
  //         >
  //           Go back to Dashboard
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  const addTask = async (col: keyof KanbanBoard['columns'], task: Task) => {
    if (!teamId) return; // Only check if teamId exists
    const ref = doc(firestore, 'kanbanBoards', teamId);
    const updated = { ...(board as KanbanBoard) };
    (updated.columns[col] as Task[]).push(task);
    await setDoc(ref, updated);
  };

  const updateTask = async (col:keyof KanbanBoard['columns'], task: Task)=>{
    if(!board||!teamId) return; // Only check if board and teamId exist
    const updated = {...board};
    const idx = updated.columns[col].findIndex(t=>t.id===task.id);
    if(idx>=0){ updated.columns[col][idx]=task; await setDoc(doc(firestore,'kanbanBoards',teamId),updated);}  };

  const deleteTask = async (col:keyof KanbanBoard['columns'], taskId:string)=>{
    if(!board||!teamId) return; // Only check if board and teamId exist
    const updated = {...board};
    updated.columns[col]=updated.columns[col].filter(t=>t.id!==taskId);
    await setDoc(doc(firestore,'kanbanBoards',teamId),updated);
  };

  const Column: React.FC<{ title: string; items: Task[]; colKey: keyof KanbanBoard['columns'] }>=({ title, items, colKey })=> {
    console.log(`🏛️ Rendering Column: ${colKey} with ${items.length} items`);
    
    // Get column order for navigation
    const columnOrder: (keyof KanbanBoard['columns'])[] = ['todo', 'inProgress', 'done'];
    const currentIndex = columnOrder.indexOf(colKey);
    const canMoveLeft = currentIndex > 0;
    const canMoveRight = currentIndex < columnOrder.length - 1;
    
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 w-full md:flex-1 md:min-w-[280px] hover:shadow-xl transition-shadow duration-300">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${colKey === 'todo' ? 'bg-blue-500' : colKey === 'inProgress' ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <span className="bg-gray-100 text-gray-600 text-sm font-medium px-2 py-1 rounded-full">{items.length}</span>
          </div>
          <button 
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg" 
            onClick={()=>setShowModalCol(colKey)}
          >
            <PlusIcon className="w-4 h-4"/>Add
          </button>
        </div>
        <div className="space-y-3 min-h-[200px]">
          {items.map((task, idx)=> {
            const isSelected = selectedTask?.task.id === task.id;
            return (
              <div key={task.id} className="relative">
                <div 
                  className={`transition-all duration-200 cursor-pointer rounded-lg ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50 shadow-lg scale-[1.02]' : 'hover:bg-gray-50 hover:shadow-md'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelected) {
                      setSelectedTask(null);
                    } else {
                      setSelectedTask({ task, col: colKey });
                    }
                  }}
                >
                  <TaskCard task={task} />
                </div>
                
                {/* Move and edit buttons when task is selected */}
                {isSelected && (
                  <div className="absolute -top-3 -right-3 flex gap-2 bg-white border border-gray-200 rounded-xl shadow-xl p-2 z-10 backdrop-blur-sm">
                    {canMoveLeft && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveTask(task, colKey, columnOrder[currentIndex - 1]);
                        }}
                        className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
                        title={`Move to ${columnOrder[currentIndex - 1] === 'todo' ? 'To Do' : 'In Progress'}`}
                      >
                        {/* Desktop (md+) shows left arrow, mobile shows up arrow */}
                        <span className="hidden md:inline">←</span>
                        <span className="md:hidden">↑</span>
                      </button>
                    )}
                    {canMoveRight && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveTask(task, colKey, columnOrder[currentIndex + 1]);
                        }}
                        className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
                        title={`Move to ${columnOrder[currentIndex + 1] === 'inProgress' ? 'In Progress' : 'Done'}`}
                      >
                        {/* Desktop (md+) shows right arrow, mobile shows down arrow */}
                        <span className="hidden md:inline">→</span>
                        <span className="md:hidden">↓</span>
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditData({col:colKey, task});
                        setSelectedTask(null);
                      }}
                      className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
                      title="Edit task"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(null);
                      }}
                      className="w-8 h-8 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {items.length===0 && colKey==='done' && (
            <div className="flex flex-col items-center text-gray-400 py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-500"/>
              </div>
              <p className="text-lg font-semibold text-gray-600 mb-1">All tasks completed!</p>
              <p className="text-sm text-gray-500">Great job! 🎉</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const doneCount = board.columns.done.length;
  const totalCount = board.columns.todo.length + board.columns.inProgress.length + doneCount;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppHeader />

      {/* Enhanced Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Kanban Board</h1>
              <p className="text-purple-100 text-lg">Organize and track your team's tasks efficiently</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-sm font-medium">Total Tasks: {totalCount}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-sm font-medium">Completed: {doneCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Task counts now shown in headers, chart removed */}
      {!canEdit && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-6 mt-6 rounded-lg shadow-sm">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                You have view-only access to this Kanban board. Contact a team admin to get edit permissions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Board */}
      <main className="flex-1 px-6 py-8">
        {/* Instructions */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm">💡</span>
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">
                <strong>How to use:</strong> Click on a task to select it and see action buttons: ← → to move between columns, ✏️ to edit, ✕ to cancel.
              </p>
            </div>
          </div>
        </div>
        
        <div 
          style={{ position: 'relative' }}
          onClick={() => setSelectedTask(null)} // Click away to deselect
        >
          {/* Columns container: stacks on mobile, horizontal from md screens upward */}
          <div className="flex flex-col md:flex-row gap-6 w-full h-full">
            <Column title="To Do" items={board.columns.todo} colKey="todo" />
            <Column title="In Progress" items={board.columns.inProgress} colKey="inProgress" />
            <Column title="Done" items={board.columns.done} colKey="done" />
          </div>
        </div>
      </main>

      <AppFooter />

      {showModalCol && (
        <TaskModal
          onClose={()=>setShowModalCol(null)}
          onSave={(task)=>{ addTask(showModalCol, task); setShowModalCol(null);} }
          teamId={teamId!}
        />
      )}

      {editData && (
        <TaskModal
          initialTask={editData.task}
          onClose={()=>setEditData(null)}
          onDelete={()=>{deleteTask(editData.col, editData.task.id); setEditData(null);}}
          onSave={(t)=>{updateTask(editData.col,t); setEditData(null);}}
          teamId={teamId!}
        />
      )}

      {onlineMembers.map((m:any,idx:number)=>{
         if(m.photoURL!==undefined && currentUser && m.displayName!==currentUser.displayName){
           if(m.x!==undefined&&m.y!==undefined){
             return <UserCursor key={idx} x={m.x} y={m.y} photoURL={m.photoURL} />;
           }
         }
         return null;
      })}
    </div>
  );
};

export default KanbanBoardPage; 